// Content for the CAM care search tab: filter and sort labels, statuses and
// dismiss reasons — the copy the real app would ship, separated from the MUI
// rendering in CareSearch.tsx. Mock providers live in CareSearch.fixtures.ts.
//
// The filter taxonomy mirrors the live Care Sourcer directory categories in
// lib/llama/src/contexts/DirectoryInteractions.tsx (careType, careProvidedTypes,
// distances, regulatorRatings, homeCareHourlyRateRanges, homeCareVisitDurations,
// sortOrders). Care Sourcer shows different filters for care homes vs home care,
// so the prototype does the same: pick a care type, get that mode's filters and
// sort options.

// The primary split. Everything else is scoped to one of these. (sentence case
// per house style; the live product uses 'Care Home' / 'Home Care'.)
export const CARE_TYPES = [
  { id: 'careHome', label: 'Care home' },
  { id: 'homeCare', label: 'Home care' },
] as const;
export type CareTypeId = (typeof CARE_TYPES)[number]['id'];

// Care Sourcer: careProvidedTypes (the kinds of care a home provides).
export const CARE_PROVIDED = [
  'Residential',
  'Nursing',
  'Dementia',
  'Palliative',
];

// Care Sourcer: distances (miles).
export const DISTANCES = [5, 10, 20];

// Care Sourcer: regulatorRatings — the CQC / RQIA / CI Wales default scale.
export const REGULATOR_RATINGS = [
  'Outstanding',
  'Good',
  'Requires improvement',
  'Inadequate',
  'Registered - awaiting routine inspection',
];

// Care Sourcer: homeCareHourlyRateRanges (home care only).
export const HOURLY_RATES = [
  'Up to £15',
  '£15 - £20',
  '£20 - £25',
  '£25 or more',
];

// Care Sourcer: homeCareVisitDurations (home care only).
export const VISIT_DURATIONS = [
  'Any duration',
  '60 minutes or less',
  '45 minutes or less',
  '30 minutes or less',
  '15 minutes',
];

export type Filter = { id: string; label: string; options: string[] };

const CARE_PROVIDED_FILTER: Filter = {
  id: 'careProvided',
  label: 'Care provided',
  options: CARE_PROVIDED,
};
const DISTANCE_FILTER: Filter = {
  id: 'distance',
  label: 'Distance',
  options: DISTANCES.map((d) => `Within ${d} miles`),
};
const RATING_FILTER: Filter = {
  id: 'rating',
  label: 'Rating',
  options: REGULATOR_RATINGS,
};
const HOURLY_RATE_FILTER: Filter = {
  id: 'hourlyRate',
  label: 'Hourly rate',
  options: HOURLY_RATES,
};
const VISIT_DURATION_FILTER: Filter = {
  id: 'visitDuration',
  label: 'Visit duration',
  options: VISIT_DURATIONS,
};

// Which filters show for each care type — mirrors Care Sourcer: care homes
// filter by what care is provided; home care filters by hourly rate and visit
// duration. Distance and rating apply to both.
export const FILTERS_BY_CARE_TYPE: Record<CareTypeId, Filter[]> = {
  careHome: [CARE_PROVIDED_FILTER, DISTANCE_FILTER, RATING_FILTER],
  homeCare: [
    HOURLY_RATE_FILTER,
    VISIT_DURATION_FILTER,
    DISTANCE_FILTER,
    RATING_FILTER,
  ],
};

// Every filter id across both modes, so the filter state can be seeded/cleared
// in one place regardless of the active care type.
export const ALL_FILTER_IDS = [
  CARE_PROVIDED_FILTER,
  DISTANCE_FILTER,
  RATING_FILTER,
  HOURLY_RATE_FILTER,
  VISIT_DURATION_FILTER,
].map((f) => f.id);

// The care type the search opens on, and filters pre-filled as if the care
// recipient's profile populated them (mini-PRD: search pre-fills from profile).
export const INITIAL_CARE_TYPE: CareTypeId = 'careHome';
export const INITIAL_FILTERS: Record<string, string> = {
  careProvided: 'Residential',
  distance: '',
  rating: 'Good',
  hourlyRate: '',
  visitDuration: '',
};

// Per-provider status within a shortlist (ENG-3413).
export const STATUSES = [
  'Not contacted',
  'Contacted',
  'Secured',
  'Unavailable',
] as const;
export type Status = (typeof STATUSES)[number];

export const STATUS_COLOR: Record<
  Status,
  'default' | 'info' | 'success' | 'error'
> = {
  'Not contacted': 'default',
  Contacted: 'info',
  Secured: 'success',
  Unavailable: 'error',
};

// The member-side "I've secured care elsewhere" declaration, read back on the
// CAM side (the member CTA is karehero-app/care/shortlists/CareSecuredCta.tsx).
// Care arranged outside KareHero is a fact about the care circle, not about one
// shortlist, so it reads as a banner over the whole shortlist pane rather than a
// status on a list.
export const SECURED_ELSEWHERE = {
  title: (recipient: string) => `${recipient} has secured care elsewhere`,
  detail: (by: string, date: string) =>
    `${by} told us on ${date}. Give them a call and close the search off.`,
};

// Dismiss reason categories. Format (dropdown, free text or both) is an open
// PRD question — this prototypes dropdown plus optional detail.
export const DISMISS_REASONS = [
  'No availability',
  'Too expensive',
  'Too far away',
  'Family declined',
  'Other',
];

// All sort options across both modes (drives the SortOption type and the
// sorters in CareSearch.tsx).
export const SORT_OPTIONS = [
  'Most relevant',
  'Nearest',
  'Highest rating',
  'Lowest price',
  'Highest price',
] as const;
export type SortOption = (typeof SORT_OPTIONS)[number];

// Care Sourcer: sortOrders — the options offered depend on the care type.
// 'Nearest to me' → 'Nearest'; home-care price sorts use the hourly rate.
export const SORT_OPTIONS_BY_CARE_TYPE: Record<CareTypeId, SortOption[]> = {
  careHome: ['Most relevant', 'Nearest', 'Highest rating'],
  homeCare: [
    'Most relevant',
    'Highest price',
    'Lowest price',
    'Highest rating',
  ],
};
